import {
  DraftRunStatus,
  IDraftMessage,
  IDraftRecommendation,
  IDraftRunVersion,
} from "../../../store/queries/contractModelDrafts";
import { PHASE_LABELS, REVISION_WORKING } from "../copy";
import { activityOf } from "../progress/progressModel";
import { isProcessing, openQuestionsOf } from "../utils";
import { WIZARD_STEPS } from "./steps";

/**
 * CLE-2811 — the run as ONE conversation. Pure helpers, so the ordering rules are tested without a DOM.
 */

export type IThreadItem =
  | { kind: "version"; key: string; versionNo: number; createdAt: string }
  /**
   * CLE-2811 — the run instructions she gave before the first generation, shown as her first message.
   * Synthetic (no message row behind it), so it is never queued and never deletable.
   */
  | { kind: "instructions"; key: string; body: string }
  | {
      kind: "message";
      key: string;
      message: IDraftMessage;
      /** Her message, not yet consumed by any pass. Only these can be deleted. */
      queued: boolean;
      /** Her optimistic message, before the server has echoed it back. */
      sending?: boolean;
    };

export const isAssistantMessage = (m: IDraftMessage): boolean =>
  m.role === "assistant";

export const isQueuedMessage = (m: IDraftMessage): boolean =>
  !isAssistantMessage(m) && !m.appliedInVersionId;

const time = (iso: string): number => new Date(iso).getTime();

/** The stable key of the run-instructions bubble: one per run, never a message id. */
export const instructionsKey = (runId: string): string =>
  `instructions:${runId}`;

/**
 * Messages and version boundaries in one chronological thread. A version separator sits at the moment
 * that pass was started, so the thread reads as "what she asked → Version N → what the agent answered".
 * Version 1 is left out: it is created with the run, before anything is said, and a separator at the
 * very top of the thread says nothing. On a timestamp tie the separator goes first — a pass's own
 * reply can never precede the pass.
 *
 * CLE-2811 — the run's instructions (`generationInstructions`), when there are any, open the thread as
 * her first bubble: they are what she said before the first generation, so they precede every message
 * and every separator. They used to sit in a card above the wizard, apart from the conversation.
 */
export const buildThread = (
  messages: IDraftMessage[],
  versions: IDraftRunVersion[],
  instructions?: { runId: string; body: string | null }
): IThreadItem[] => {
  const items: { at: number; order: number; item: IThreadItem }[] = [];
  versions
    .filter((v) => v.versionNo > 1)
    .forEach((v) =>
      items.push({
        at: time(v.createdAt),
        order: 0,
        item: {
          kind: "version",
          key: `version-${v.id}`,
          versionNo: v.versionNo,
          createdAt: v.createdAt,
        },
      })
    );
  messages.forEach((m) =>
    items.push({
      at: time(m.createdAt),
      order: 1,
      item: {
        kind: "message",
        key: m.id,
        message: m,
        queued: isQueuedMessage(m),
      },
    })
  );
  // Array.prototype.sort is stable, so equal (at, order) keep their server order.
  const thread = items
    .sort((a, b) => a.at - b.at || a.order - b.order)
    .map((x) => x.item);
  const body = instructions?.body?.trim();
  if (!instructions || !body) return thread;
  return [
    { kind: "instructions", key: instructionsKey(instructions.runId), body },
    ...thread,
  ];
};

/**
 * Every distinct open question on the current version, in recommendation order. A withdrawn
 * (`superseded`) or rejected recommendation's questions are moot — nobody is building it.
 */
export const collectOpenQuestions = (
  recs: IDraftRecommendation[]
): string[] => {
  const seen = new Set<string>();
  const out: string[] = [];
  recs
    .filter((rec) => rec.status !== "superseded" && rec.status !== "rejected")
    .forEach((rec) =>
      openQuestionsOf(rec).forEach((q) => {
        if (!seen.has(q)) {
          seen.add(q);
          out.push(q);
        }
      })
    );
  return out;
};

// ---------------------------------------------------------------------------------------------
// Schedule blocks — a rate list inside a reply
// ---------------------------------------------------------------------------------------------

/**
 * One schedule row: a code, then an amount — "27130 14657", "G0105 $250.00", "0360: 1,234.50",
 * "27447-RT — 12,000", "J1885 150%". The code is a CPT / HCPCS / revenue code (3–5 letters or digits,
 * optionally a modifier); the amount may carry a "$", thousands commas, decimals or a "%".
 */
const SCHEDULE_ROW =
  /^\s*[A-Z0-9]{3,5}(?:-[A-Z0-9]{2})?\s*(?:[:=|–—-]\s*|\s)\s*\$?\d[\d,]*(?:\.\d+)?\s*%?\s*$/i;

/** At least this many consecutive rows make a schedule block; fewer read as ordinary text. */
export const SCHEDULE_MIN_ROWS = 3;

export const isScheduleRow = (line: string): boolean => SCHEDULE_ROW.test(line);

export interface ITextSegment {
  kind: "text" | "schedule";
  text: string;
}

/**
 * CLE-2811 — a reply can carry a whole rate list ("List every CPT code and rate, one per line"). Runs of
 * SCHEDULE_MIN_ROWS or more consecutive "<code> <amount>" lines become a `schedule` segment, shown in a
 * monospace block so the columns line up; everything else stays `text`. Two rows are just a sentence's
 * worth and stay text. Joining the segments' text with "\n" gives back the input.
 */
export const splitScheduleBlocks = (text: string): ITextSegment[] => {
  const lines = text.split("\n");
  const out: ITextSegment[] = [];
  let plain: string[] = [];
  const flushPlain = () => {
    if (plain.length) out.push({ kind: "text", text: plain.join("\n") });
    plain = [];
  };
  let i = 0;
  while (i < lines.length) {
    let j = i;
    while (j < lines.length && isScheduleRow(lines[j])) j += 1;
    if (j - i >= SCHEDULE_MIN_ROWS) {
      flushPlain();
      out.push({ kind: "schedule", text: lines.slice(i, j).join("\n") });
      i = j;
    } else {
      plain.push(lines[i]);
      i += 1;
    }
  }
  flushPlain();
  return out;
};

// ---------------------------------------------------------------------------------------------
// The live reply — the AI's words while a pass runs
// ---------------------------------------------------------------------------------------------

/** The narration the live reply bubble shows, remembered for the pass it belongs to. */
export interface ILiveReplyMemory {
  /**
   * The pass the text belongs to (`passKeyOf`): the version it writes into, else that version's number; null
   * when nothing is remembered.
   */
  versionNo: number | string | null;
  text: string;
}

export const EMPTY_LIVE_REPLY: ILiveReplyMemory = { versionNo: null, text: "" };

/**
 * CLE-2811 (in place) — which pass a live reply belongs to: the version it writes into (`latestVersionId`), else
 * the run's `versionNo`. During a revision pass `versionNo` is the version ON SCREEN, which a chat change applied
 * in place keeps — so two chained passes share it, while each writes into its own new version.
 */
const passKeyOf = (run: {
  versionNo: number;
  latestVersionId?: string | null;
}): number | string => run.latestVersionId ?? run.versionNo;

/**
 * CLE-2811 — while a pass runs, the thread's last item is the AI's reply as it is being written: the
 * worker's streamed `progress.narration`. The worker clears its progress when it deposits, so for the few
 * seconds of `worker_returned` the last words seen are kept rather than falling back to "Thinking…".
 * Forgotten as soon as the run is not generating, or a new version starts (a chained pass), so one pass's
 * words never appear under the next.
 */
export const rememberNarration = (
  prev: ILiveReplyMemory,
  run: {
    status: DraftRunStatus;
    versionNo: number;
    latestVersionId?: string | null;
    progress: { narration?: string | null } | null;
  }
): ILiveReplyMemory => {
  if (!isProcessing(run.status)) return EMPTY_LIVE_REPLY;
  const pass = passKeyOf(run);
  const narration =
    typeof run.progress?.narration === "string" ? run.progress.narration : "";
  if (narration.trim())
    return prev.versionNo === pass && prev.text === narration
      ? prev
      : { versionNo: pass, text: narration };
  return prev.versionNo === pass ? prev : { versionNo: pass, text: "" };
};

/** The activity feed the live reply falls back to, remembered for the pass it belongs to. */
export interface ILiveActivityMemory {
  /** The pass the lines belong to (`passKeyOf`); null when nothing is remembered. */
  versionNo: number | string | null;
  lines: string[];
}

export const EMPTY_LIVE_ACTIVITY: ILiveActivityMemory = {
  versionNo: null,
  lines: [],
};

/**
 * CLE-2811 — the worker's `progress.activity` (its last actions in plain words), kept through
 * `worker_returned` exactly as `rememberNarration` keeps the narration. The fast facts pipeline streams no
 * text: it reports steps ("Found 4 plans", "Reading TRAD's terms…") as activity, so without this the live
 * reply would sit on "Thinking…" for a whole facts pass. Pure: returns `prev` when nothing changed.
 */
export const rememberActivity = (
  prev: ILiveActivityMemory,
  run: {
    status: DraftRunStatus;
    versionNo: number;
    latestVersionId?: string | null;
    progress: { activity?: string[] | null } | null;
  }
): ILiveActivityMemory => {
  if (!isProcessing(run.status)) return EMPTY_LIVE_ACTIVITY;
  const pass = passKeyOf(run);
  const lines = activityOf(
    run.progress as Parameters<typeof activityOf>[0] | null
  );
  if (lines.length) {
    const same =
      prev.versionNo === pass &&
      prev.lines.length === lines.length &&
      prev.lines.every((l, i) => l === lines[i]);
    return same ? prev : { versionNo: pass, lines };
  }
  return prev.versionNo === pass ? prev : { versionNo: pass, lines: [] };
};

/** The phase words the revision line falls back to when the pass gives no detail (the strip's timeline labels). */
const REVISION_PHASE_LABELS: Record<string, string> = {
  downloading: PHASE_LABELS.downloading,
  extracting: PHASE_LABELS.extracting,
  thinking: PHASE_LABELS.thinking,
  depositing: PHASE_LABELS.saving,
};

/**
 * CLE-2811 (in place) — the live reply's line while a REVISION pass runs (`revisionInFlight`) and the AI has
 * written no narration: what the pass says it is doing (`progress.detail`, e.g. "Updating the Cigna PPO
 * inpatient reimbursement"), else its phase ("AI modeling"), else — queued, or no progress yet — that it is
 * working on her change; with a trailing "…" unless the text already has one ("Re-reading SuperMed… 0 of 1
 * done"). The page shows no progress strip during a revision pass: this line is the progress. Pure.
 */
export const revisionProgressLine = (run: {
  status: DraftRunStatus;
  progress: { phase?: string | null; detail?: string | null } | null;
}): string => {
  const detail =
    typeof run.progress?.detail === "string" ? run.progress.detail.trim() : "";
  const phase =
    run.status === "queued"
      ? PHASE_LABELS.queued
      : REVISION_PHASE_LABELS[run.progress?.phase ?? ""] ?? "";
  const line = detail || phase || REVISION_WORKING;
  return line.includes("…") ? line : `${line.replace(/\.+$/, "")}…`;
};

/** What the live reply bubble shows: the AI's words, else its step-by-step activity, else "Thinking…". */
export type LiveReplyContent =
  | { kind: "narration"; text: string }
  | { kind: "activity"; lines: string[] }
  | { kind: "thinking" };

/**
 * CLE-2811 — the live reply's content. Narration wins (it is the AI's own reply being written); with none,
 * the activity feed, so the bubble is never an empty "Thinking…" during a facts-pipeline pass that
 * reports steps instead of streaming text; with neither, "Thinking…".
 */
export const liveReplyContent = (
  narration: string | null | undefined,
  activity: string[] | null | undefined
): LiveReplyContent => {
  if (typeof narration === "string" && narration.trim())
    return { kind: "narration", text: narration };
  const lines = Array.isArray(activity)
    ? activity.filter((l) => typeof l === "string" && l.trim() !== "")
    : [];
  if (lines.length) return { kind: "activity", lines };
  return { kind: "thinking" };
};

/** "Contract Terms" for `terms`; null for no step or one the wizard no longer has. */
export const stepTitle = (stepId: string | null): string | null =>
  stepId ? WIZARD_STEPS.find((s) => s.id === stepId)?.title ?? null : null;

/** "15:20" — the thread is read on the day it is written; the date would be noise. */
export const formatThreadTime = (iso: string): string =>
  new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
